import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import NavDropdown from "react-bootstrap/NavDropdown";
import Container from "react-bootstrap/Container";

import PlanUsageV2, { PlanUsageNavbar } from "./usage/plan";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { IonToggle } from "@ionic/react";
import classNames from "classnames";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

import {
  IconLogout,
  IconSettings,
  IconBook2,
  IconBell,
} from "@tabler/icons-react";

import "./NavBar.css";
import { User, Settings } from "../interfaces";
import config from "../config";
import AccountDropDown from "./common/account-drop-down";
import { useNotification } from "../providers/desktop-notification";
import useCountersStore from "../store/counts";
import { useAuth } from "../providers/auth";
import NavBarAiItem from "./NavBarAiItem";
import NavBarWhatsNewItem from "./NavBarWhatsNewItem";

const NavBar: React.FC<ILayoutProps> = (props: ILayoutProps) => {
  const { user, path, settings, switchCompany, display } = props;
  const isAdmin = user.role === "admin" || user.role === "system";
  const hasConnectedChannel =
    settings.onboardingList?.find((item) => item.name === "channel")?.done ===
    true;
  const {
    permission,
    notificationsEnabled,
    setNotificationsEnabled,
    requestPermission,
  } = useNotification();

  const total = useCountersStore((state) => state.total);

  const { logout } = useAuth();

  const handleToggle = async (checked: boolean) => {
    if (checked) {
      const result = await requestPermission();
      if (result === "granted") {
        setNotificationsEnabled(true);
      }
    } else {
      setNotificationsEnabled(false);
    }
  };

  return display ? (
    <Navbar className="bg-body-tertiary visito-navbar">
      <Container fluid>
        <Navbar.Brand as={Link} to={"/"}>
          <div className="logo"></div>
        </Navbar.Brand>
        <Nav className="me-auto">
          <Nav.Link
            className={classNames("menu-list-link", {
              active: path === "/",
            })}
            as={Link}
            to="/"
          >
            <FormattedMessage id="nav.home" />
            {/*
              Not to deploy now
              {settings.badge ? (
                <span className="badge badge-visito">{settings.badge}</span>
              ) : null}
            */}
          </Nav.Link>
          <Nav.Link
            className={classNames("menu-list-link relative", {
              active: path.includes("/chats"),
              notifications: !path.includes("/chats") && total > 0,
            })}
            as={Link}
            to="/chats"
          >
            <FormattedMessage id="nav.inbox" />
            {path.includes("/chats") || total === 0 ? null : (
              <span className="notif-pill abs btn-visito-danger rounded-pill fw-bold">
                {total > 99 ? "99+" : total}
              </span>
            )}
          </Nav.Link>

          <Nav.Link
            className={classNames("navigation-link", {
              active: path.includes("/leads"),
            })}
            as={Link}
            to="/leads"
          >
            <FormattedMessage id="nav.leads" />
          </Nav.Link>
          {settings.paymentsEligible && isAdmin ? (
            <Nav.Link
              className={classNames("navigation-link", {
                active: path.includes("/payments"),
              })}
              as={Link}
              to="/payments"
            >
              <FormattedMessage id="nav.payments" />
            </Nav.Link>
          ) : null}

          {settings.events && isAdmin ? (
            <Nav.Link
              className={classNames("navigation-link", {
                active: path.includes("/outbound"),
              })}
              as={Link}
              to="/outbound"
            >
              <FormattedMessage id="nav.outbound" />
            </Nav.Link>
          ) : null}
          {isAdmin && (
            <Nav.Link
              className={classNames("navigation-link flex align-items-center gap-1", {
                active: path.includes("/ai") && !path.includes("/ai-settings"),
              })}
              as={Link}
              to="/ai"
            >
              <FormattedMessage id="nav.ai" />
            </Nav.Link>
          )}
        </Nav>

        {isAdmin && hasConnectedChannel ? <NavBarWhatsNewItem /> : null}

        {isAdmin ? <NavBarAiItem path={path} companyId={settings.company.id} /> : null}

        <NavDropdown
          title={
            <div className="text-holder">
              <PlanUsageNavbar settings={settings} isMobile={false} />
            </div>
          }
          className="subscribe-cta"
        >
          <PlanUsageV2 settings={settings} showCta={true} isMobile={false} />
        </NavDropdown>

        <NavDropdown
          title={
            <div className="company-presentation">
              <div className="company-name">{settings.company.name}</div>
            </div>
          }
          className="account-switcher"
        >
          <NavDropdown.Header>{user.email}</NavDropdown.Header>
          <NavDropdown.Item as={Link} to="/settings">
            <IconSettings size={16} style={{ marginTop: "-2px" }} />{" "}
            <FormattedMessage id="nav.settings" />
          </NavDropdown.Item>
          {/*<NavDropdown.Item as={Link} to="https://www.visitoai.com/en/guide">
              <IconMessages size={16} style={{ marginTop: "-2px" }} />{" "}
              <FormattedMessage id="nav.helpChat" />
            </NavDropdown.Item>
            */}
          <NavDropdown.Item
            as="a"
            href={config.HELP_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <IconBook2 size={16} style={{ marginTop: "-2px" }} />{" "}
            <FormattedMessage id="nav.helpCenter" />
          </NavDropdown.Item>
          <NavDropdown.Header className="uppercase">
            <FormattedMessage id="nav.accountList" />{" "}
            {settings.companies.length > 1
              ? `(${settings.companies.length})`
              : ""}
          </NavDropdown.Header>
          <AccountDropDown
            currentCompany={settings.company}
            companies={settings.companies}
            onSelectCompany={(id) => switchCompany({ companyId: id })}
          />
          <NavDropdown.Item
            as={Link}
            to="/account/create"
            className="account-switcher-create-account"
          >
            + <FormattedMessage id="nav.createAccount" />
          </NavDropdown.Item>
          <NavDropdown.Divider />
          <NavDropdown.Item onClick={(e) => e.stopPropagation()}>
            <div className="d-flex align-items-center justify-content-between">
              <OverlayTrigger
                placement="bottom"
                delay={{ show: 250, hide: 400 }}
                overlay={
                  <Tooltip className="visito-tooltip">
                    <FormattedMessage id="nav.notificationsToolTip" />
                  </Tooltip>
                }
              >
                <div>
                  <IconBell size={16} style={{ marginTop: "-2px" }} />{" "}
                  <FormattedMessage id="nav.notifications" />
                </div>
              </OverlayTrigger>
              <IonToggle
                checked={notificationsEnabled && permission === "granted"}
                onIonChange={(ev) => handleToggle(ev.detail.checked)}
                mode="ios"
                className="ios"
              ></IonToggle>
            </div>
          </NavDropdown.Item>

          <NavDropdown.Divider />
          <NavDropdown.Item
            onClick={(e) => {
              e.preventDefault();
              logout();
            }}
          >
            <IconLogout size={16} style={{ marginTop: "-2px" }} />{" "}
            <FormattedMessage id="nav.logout" />
          </NavDropdown.Item>
        </NavDropdown>
      </Container>
    </Navbar>
  ) : null;
};

interface ILayoutProps {
  user: User;
  switchCompany: Function;
  settings: Settings;
  path: string;
  display: boolean;
}

export default NavBar;
